import BreadCrumb from "Common/BreadCrumb";
import Loader from "Common/Components/Loader";

import TableContainer from "Common/TableContainer";
import { Plus, Search } from "lucide-react";
import React, { useEffect, useMemo, useState } from "react";
import { ToastContainer } from "react-toastify";
import {
  useCreateQuestionSet,
  useQuestions,
  useQuestionSets,
} from "./useTrivia.js";
import { Link } from "Common/SafeLink";
import Modal from "Common/Components/Modal";

const formatDate = (dateString: string): string => {
  // Convert the input string to a Date object
  const date = new Date(dateString);

  // Get the day, month, and year parts
  const day = String(date.getDate()).padStart(2, "0");
  const month = String(date.getMonth() + 1).padStart(2, "0"); // Months are 0-indexed
  const year = date.getFullYear();

  // Return the formatted date string
  return `${day} - ${month} - ${year}`;
};

const filterDataBySearch = (data, search, keysToSearch, setFilteredData) => {
  const lowerCaseSearch = search.toLowerCase();
  const filtered = data.filter((item) =>
    keysToSearch.some((key) => {
      const value = item[key];
      if (value != null) {
        return value.toString().toLowerCase().includes(lowerCaseSearch);
      }
      return false;
    })
  );
  setFilteredData(filtered);
};
const QuestionSet = () => {
  const { questionSets, isLoading } = useQuestionSets();
  const { createQuestionSet } = useCreateQuestionSet();
  const { questions } = useQuestions();
  const questionsLength = questions?.length;

  const [data, setData] = useState<any>([]);
  const [newQuestionSet, setNewQuestionSet] = useState({
    name: "",
    numberOfQuestions: 0,
  });
  const [showModal, setShowModal] = useState(false);
  const [errors, setErrors] = useState<{
    name?: string;
    numberOfQuestions?: string;
  }>({});

  const handleSave = () => {
    const { name, numberOfQuestions } = newQuestionSet;
    let newErrors: { name?: string; numberOfQuestions?: string } = {};

    if (!name.trim()) {
      newErrors.name = "Please enter a question set title.";
    }

    if (numberOfQuestions < 1 || numberOfQuestions > questionsLength) {
      newErrors.numberOfQuestions = `Please enter a number between 1 and ${questionsLength}.`;
    }

    setErrors(newErrors);

    if (Object.keys(newErrors).length > 0) return; // Stop if there are validation errors

    // Shuffle and select random question IDs
    const shuffledQuestions = [...questions].sort(() => 0.5 - Math.random());
    const selectedQuestions = shuffledQuestions
      .slice(0, numberOfQuestions)
      .map((q) => q.id);

    // Send request to create question set
    createQuestionSet({ name, questionsId: selectedQuestions });

    // Close modal and reset form
    setShowModal(false);
    setNewQuestionSet({ name: "", numberOfQuestions: 0 });
    setErrors({});
  };

  useEffect(() => {
    setData(questionSets);
  }, [questionSets]);

  const filterSearchData = (e: any) => {
    const search = e.target.value;
    const keysToSearch = ["name", "numberOfQuestions"];
    filterDataBySearch(questionSets, search, keysToSearch, setData);
  };

  const handleInputChange = (e, index, field) => {
    const value = e.target.value;
    setNewQuestionSet((prev) => {
      const updated = { ...prev };
      if (field === "name") {
        updated.name = value;
      } else if (field === "numberOfQuestions") {
        updated.numberOfQuestions = value;
      }
      return updated;
    });
  };
  const columns = useMemo(
    () => [
      {
        header: "Title",
        accessorKey: "name",
        enableColumnFilter: false,
        enableSorting: false,
      },
      {
        header: "NumberOfQuestions",
        accessorKey: "numberOfQuestions",
        enableColumnFilter: false,
      },
      {
        header: "Date",
        accessorKey: "lastModifiedAt",
        enableColumnFilter: false,
        cell: (cell: any) => {
          return <p>{formatDate(cell?.row.original.lastModifiedAt)}</p>;
        },
      },
      {
        header: "Actions",
        accessorKey: "phoneNumber",
        enableColumnFilter: false,
        cell: (cell: any) => (
          <Link to={`/questions-set/${cell.row.original.id}`}>
            <p className="underline text-blue-700">Show Questions</p>
          </Link>
        ),
      },
    ],
    []
  );

  return (
    <React.Fragment>
      <BreadCrumb title="Questions Set" pageTitle="Trivia" />
      <button
        className="px-4 py-2 bg-blue-500 mb-4 justify-self-end text-white rounded hover:bg-blue-600 flex items-center"
        onClick={() => setShowModal(true)}
      >
        <Plus className="mr-2" /> Add Question Set
      </button>
      {isLoading && <Loader />}
      <ToastContainer closeButton={false} limit={1} />
      <div className="card" id="ordersTable">
        <div className="card-body">
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-12">
            <div className="lg:col-span-3">
              <div className="relative">
                <input
                  type="text"
                  className="ltr:pl-8 rtl:pr-8 search form-input border-slate-200 dark:border-zink-500 focus:outline-none focus:border-custom-500 disabled:bg-slate-100 dark:disabled:bg-zink-600 disabled:border-slate-300 dark:disabled:border-zink-500 dark:disabled:text-zink-200 disabled:text-slate-500 dark:text-zink-100 dark:bg-zink-700 dark:focus:border-custom-800 placeholder:text-slate-400 dark:placeholder:text-zink-200"
                  placeholder="Search by Title or Number of Questions"
                  autoComplete="off"
                  onChange={(e) => filterSearchData(e)}
                />
                <Search className="inline-block size-4 absolute ltr:left-2.5 rtl:right-2.5 top-2.5 text-slate-500 dark:text-zink-200 fill-slate-100 dark:fill-zink-600" />
              </div>
            </div>
            <div className="lg:col-span-2 lg:col-start-11">
              <div className="ltr:lg:text-right rtl:lg:text-left"></div>
            </div>
          </div>

          {data && data.length > 0 ? (
            <TableContainer
              isPagination={true}
              columns={columns || []}
              data={data || []}
              customPageSize={10}
              divclassName="mt-5 overflow-x-auto"
              tableclassName="w-full whitespace-nowrap"
              theadclassName="ltr:text-left rtl:text-right bg-slate-100 dark:bg-zink-600"
              thclassName="px-3.5 py-2.5 font-semibold text-slate-500 border-b border-slate-200 dark:border-zink-500 dark:text-zink-200"
              tdclassName="px-3.5 py-2.5 border-y border-slate-200 dark:border-zink-500"
              PaginationClassName="flex flex-col items-center mt-5 md:flex-row"
            />
          ) : (
            <div className="noresult">
              <div className="py-6 text-center">
                <Search className="size-6 mx-auto text-sky-500 fill-sky-100 dark:sky-500/20" />
                <h5 className="mt-2 mb-1">Sorry! No Result Found</h5>
                <p className="mb-0 text-slate-500 dark:text-zink-200">
                  We've searched more than 299+ Question Sets and found no
                  results for your search.
                </p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Modal for Adding Questions */}
      <Modal
        show={showModal}
        onHide={() => setShowModal(false)}
        modal-center="true"
        className="fixed flex flex-col transition-all duration-300 ease-in-out left-2/4 z-drawer -translate-x-2/4 -translate-y-2/4"
        dialogClassName="w-screen md:w-[32rem] bg-white shadow-lg rounded-md dark:bg-gray-800"
      >
        <Modal.Header className="flex items-center justify-between p-5 border-b dark:border-gray-600">
          <h2 className="text-xl font-bold text-gray-800 dark:text-white">
            Add New Question Set
          </h2>
        </Modal.Header>

        <Modal.Body className="p-6 space-y-5">
          <div>
            <label className="block text-gray-700 font-semibold mb-1">
              Question Set Title
            </label>
            <input
              type="text"
              className="w-full p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:outline-none"
              value={newQuestionSet.name}
              onChange={(e) => handleInputChange(e, null, "name")}
              placeholder="Enter question set title..."
            />
            {errors.name && (
              <p className="text-red-500 text-sm mt-1">{errors.name}</p>
            )}
          </div>

          <div>
            <label className="block text-gray-700 font-semibold mb-1">
              Number Of Questions
            </label>
            <input
              type="number"
              className="w-full p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-green-500 focus:outline-none"
              value={newQuestionSet.numberOfQuestions}
              onChange={(e) => handleInputChange(e, null, "numberOfQuestions")}
              placeholder={`Enter a number between 1 and ${questionsLength}`}
            />
            {errors.numberOfQuestions && (
              <p className="text-red-500 text-sm mt-1">
                {errors.numberOfQuestions}
              </p>
            )}
          </div>
        </Modal.Body>

        <Modal.Footer className="p-5 flex justify-end space-x-4">
          <button
            className="px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-all"
            onClick={() => setShowModal(false)}
          >
            Cancel
          </button>
          <button
            className="px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-all"
            onClick={handleSave}
          >
            Save
          </button>
        </Modal.Footer>
      </Modal>
    </React.Fragment>
  );
};

export default QuestionSet;
